import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { useLang } from "@/lib/i18n";
import { repeatOf, byTime } from "@/lib/schedule";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, MapPin, ArrowRight, Flower2, Users, Images, Newspaper } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1603766806347-54cdf3745953?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ABOUT_IMG = "https://images.unsplash.com/photo-1758797316894-c25b94e8426a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [news, setNews] = useState([]);
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState({ members: 0, events: 0, schedules: 0, gallery: 0 });
  const [content, setContent] = useState({});
  const { t, tr } = useLang();

  useEffect(() => {
    api.get("/public/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/schedules").then((r) => setSchedules(r.data.filter((s) => repeatOf(s) === "daily").sort(byTime).slice(0, 6))).catch(() => {});
    api.get("/public/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
    api.get("/public/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/content/home_hero").then((r) => setContent(r.data || {})).catch(() => {});
  }, []);

  const hero = banners[0];
  const heroImg = hero?.image_path ? fileUrl(hero.image_path) : HERO;
  const heroTitle = hero?.title || content.title || "Hatha Vidya";
  const [titleMain, titleRest] = heroTitle.includes("Charitable Trust") ? [heroTitle.replace(/\s*Charitable Trust.*$/, ""), heroTitle.slice(heroTitle.indexOf("Charitable Trust"))] : [heroTitle, "Charitable Trust"];
  const heroSubtitle = (hero && tr(hero, "subtitle")) || tr(content, "subtitle") || "Serving the community through yoga, spiritual learning, and humanitarian seva. Join us in a journey of discipline, devotion and service.";

  return (
    <div data-testid="public-home">
      <section className="relative min-h-[70vh] sm:min-h-[85vh] flex items-center overflow-hidden" data-testid="home-hero">
        <img src={heroImg} alt="Hatha Vidya Charitable Trust" className="absolute inset-0 h-full w-full object-cover object-[65%_15%] sm:object-[center_6%]" />
        <div className="absolute inset-0 bg-[#0E2916]/40 sm:bg-[#0E2916]/20" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(14,41,22,0.88) 0%, rgba(14,41,22,0.62) 55%, rgba(14,41,22,0.5) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 w-full">
          <div className="max-w-2xl">
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#C59B27] font-mono mb-3 sm:mb-4 flex items-center gap-2"><Flower2 className="h-4 w-4" /> {tr(content, "eyebrow") || "Gandhidham · Kutch · Gujarat"}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.15] sm:leading-[1.1] drop-shadow-sm" data-testid="home-hero-title">{titleMain}<br /><span className="text-[#C59B27]">{titleRest}</span></h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl" data-testid="home-hero-subtitle">{heroSubtitle}</p>
            <div className="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
              <Link to="/membership"><Button className="rounded-full bg-[#C59B27] hover:bg-[#A8811A] text-white font-semibold h-12 px-7 text-base active:scale-[0.98]" data-testid="hero-join-button">{tr(content, "cta_text") || t("Become a Member")}</Button></Link>
              <Link to="/donate"><Button variant="outline" className="rounded-full h-12 px-7 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">{t("Support Us")}</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[{ v: stats.members, l: "Members", i: Users }, { v: stats.events, l: "Events", i: CalendarDays }, { v: stats.schedules, l: "Daily Programs", i: Clock }, { v: stats.gallery, l: "Photos", i: Images }].map((s, idx) => (
            <div key={idx}><s.i className="h-5 w-5 mx-auto text-[#C59B27] mb-2" /><div className="text-3xl font-bold">{s.v}+</div><div className="text-xs uppercase tracking-widest text-white/60 font-mono mt-1">{t(s.l)}</div></div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <img src={ABOUT_IMG} alt="Yoga" className="rounded-2xl shadow-xl w-full h-[420px] object-cover" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("About the Trust")}</p>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#194324] mt-2 leading-snug">{t("Rooted in yoga, devoted to service")}</h2>
          <p className="mt-5 text-[#3A4E41] leading-relaxed">{t("Based in Gandhidham, the Hatha Vidya Charitable Trust nurtures the ancient science of Hatha Yoga alongside selfless community welfare across the Kutch region.")}</p>
          <Link to="/about"><Button className="mt-8 rounded-full bg-[#194324] hover:bg-[#2D623A] text-white">{t("Learn More")} <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
        </div>
      </section>

      {/* Activities grid */}
      <section className="bg-[#F2EEE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("What We Do")}</p>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#194324] mt-2">{t("Our Activities")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={`/programs/${c.key}`} className="group relative h-56 rounded-xl overflow-hidden" data-testid={`home-activity-${c.key}`}>
                <img src={c.hero} alt={c.label} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(14,41,22,0.9) 0%, rgba(14,41,22,0.2) 70%)" }} />
                <div className="absolute bottom-0 p-5">
                  <h3 className="font-display text-2xl font-semibold text-white">{tr(c, "label")}</h3>
                  <span className="text-[#C59B27] text-sm flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">{t("Explore")} <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("What's Happening")}</p><h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#194324] mt-2">{t("Upcoming Events")}</h2></div>
          <Link to="/events" className="text-[#194324] font-medium flex items-center gap-1 hover:gap-2 transition-all">{t("All events")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {events.length === 0 ? (
          <p className="text-[#5C7264]">{t("No upcoming events published yet. Please check back soon.")}</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((e) => (
              <div key={e.id} className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`home-event-${e.id}`}>
                <div className="h-44 bg-[#E8F0EA]">{e.image_path ? <img src={fileUrl(e.image_path)} alt={e.name} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><CalendarDays className="h-10 w-10 text-[#C59B27]/40" /></div>}</div>
                <div className="p-5">
                  <div className="inline-flex items-center gap-2 text-xs font-mono text-[#B38728] bg-[#F9F3E3] rounded-full px-3 py-1"><CalendarDays className="h-3 w-3" />{e.date}</div>
                  <h3 className="font-display text-xl font-semibold text-[#194324] mt-3">{tr(e, "name")}</h3>
                  {e.location && <p className="text-sm text-[#5C7264] mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location}</p>}
                  <p className="text-sm text-[#3A4E41] mt-2 line-clamp-2">{tr(e, "description")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* News */}
      {news.length > 0 && (
        <section className="bg-[#F2EEE5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("Updates")}</p><h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#194324] mt-2">{t("Latest News")}</h2></div>
              <Link to="/news" className="text-[#194324] font-medium flex items-center gap-1 hover:gap-2 transition-all">{t("All news")} <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {news.map((n) => (
                <Link key={n.id} to="/news" className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all">
                  {n.image_path ? <div className="h-40 bg-[#E8F0EA]"><img src={fileUrl(n.image_path)} alt={n.title} className="h-full w-full object-cover" /></div> : <div className="h-40 bg-[#E8F0EA] flex items-center justify-center"><Newspaper className="h-10 w-10 text-[#C59B27]/40" /></div>}
                  <div className="p-5"><div className="text-xs font-mono text-[#B38728]">{n.category}{n.date && ` · ${n.date}`}</div><h3 className="font-display text-lg font-semibold text-[#194324] mt-1">{tr(n, "title")}</h3><p className="text-sm text-[#3A4E41] mt-2 line-clamp-2">{tr(n, "excerpt") || tr(n, "content")}</p></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schedule preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-10"><p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("Daily Rhythm")}</p><h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#194324] mt-2">{t("Ashram Schedule")}</h2></div>
        {schedules.length === 0 ? (
          <p className="text-center text-[#5C7264]">{t("Schedule will be published soon.")}</p>
        ) : (
          <div className="max-w-3xl mx-auto space-y-3">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center gap-5 bg-white rounded-xl border border-[#E2DAD0] p-4 hover:border-[#C59B27] transition-colors">
                <div className="w-24 shrink-0 text-center"><div className="font-mono text-[#C59B27] font-semibold">{s.time}</div></div>
                <div className="flex-1 border-l border-[#E2DAD0] pl-5"><div className="font-semibold text-[#194324]">{tr(s, "program")}</div>{s.location && <div className="text-sm text-[#5C7264] flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location}</div>}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-[#0E2916]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Moments")}</p><h2 className="font-display text-3xl lg:text-4xl font-semibold text-white mt-2">{t("Gallery")}</h2></div>
              <Link to="/gallery" className="text-[#C59B27] font-medium flex items-center gap-1 hover:gap-2 transition-all">{t("View all")} <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((g) => (<div key={g.id} className="aspect-[4/3] rounded-xl overflow-hidden group"><img src={fileUrl(g.image_path)} alt={g.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="bg-gradient-to-br from-[#194324] to-[#0E2916] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#C59B27]/15 blur-3xl" />
          <Flower2 className="h-10 w-10 text-[#C59B27] mx-auto mb-4" />
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-white">{t("Join the Hatha Vidya family")}</h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">{t("Become a member, volunteer or donor and be a part of our seva.")}</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/membership"><Button className="rounded-full bg-[#C59B27] hover:bg-[#A8811A] text-white font-semibold h-12 px-8" data-testid="cta-join-button">{t("Become a Member")}</Button></Link>
            <Link to="/volunteer"><Button variant="outline" className="rounded-full h-12 px-8 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">{t("Volunteer")}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
